/**
 * Onboarding Whiteboard — dynamic canvas texture for the whiteboard surface.
 * Writes task text in a hand-drawn style, and crosses it off when complete.
 */

import * as THREE from 'three';

const CANVAS_W = 512;
const CANVAS_H = 340;

export class OnboardingWhiteboard {
  private scene: THREE.Scene;
  private surface: THREE.Mesh | null = null;
  private originalMaterial: THREE.Material | null = null;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: THREE.CanvasTexture | null = null;
  private currentText = '';

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    this.ctx = this.canvas.getContext('2d')!;

    // Find whiteboard surface mesh
    const wb = scene.getObjectByName('whiteboard') as THREE.Group | undefined;
    if (wb && wb.children.length >= 2) {
      this.surface = wb.children[1] as THREE.Mesh;
      this.originalMaterial = this.surface.material as THREE.Material;
    }
  }

  /**
   * Write task text on the whiteboard with a marker-style font.
   */
  writeTask(text: string): void {
    if (!this.surface) return;
    this.currentText = text;

    const ctx = this.ctx;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Title area — small grey header
    ctx.fillStyle = '#999999';
    ctx.font = 'bold 18px system-ui, sans-serif';
    ctx.fillText('TASKS', 30, 40);

    // Divider line
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, 52);
    ctx.lineTo(CANVAS_W - 30, 52);
    ctx.stroke();

    // Bullet point
    ctx.fillStyle = '#6366f1';
    ctx.beginPath();
    ctx.arc(44, 90, 6, 0, Math.PI * 2);
    ctx.fill();

    // Task text — dark, bold, marker-style
    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'bold 26px system-ui, sans-serif';

    // Word-wrap the text
    const words = text.split(' ');
    let line = '';
    let y = 98;
    const maxWidth = CANVAS_W - 100;

    for (const word of words) {
      const testLine = line + (line ? ' ' : '') + word;
      if (ctx.measureText(testLine).width > maxWidth && line) {
        ctx.fillText(line, 60, y);
        line = word;
        y += 34;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, 60, y);

    this.applyTexture();
  }

  /**
   * Draw a red strikethrough across the task text.
   */
  crossOffTask(): void {
    if (!this.surface || !this.currentText) return;

    const ctx = this.ctx;

    // Red strikethrough line — slightly angled for hand-drawn feel
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(55, 92);
    ctx.lineTo(CANVAS_W - 50, 88);
    ctx.stroke();

    // Checkmark next to bullet
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(34, 90);
    ctx.lineTo(40, 96);
    ctx.lineTo(54, 78);
    ctx.stroke();

    this.applyTexture();
  }

  /**
   * Restore original white material.
   */
  clear(): void {
    if (!this.surface || !this.originalMaterial) return;
    this.surface.material = this.originalMaterial;
    this.currentText = '';

    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }
  }

  private applyTexture(): void {
    if (!this.surface) return;

    if (this.texture) {
      this.texture.needsUpdate = true;
    } else {
      this.texture = new THREE.CanvasTexture(this.canvas);
      this.texture.colorSpace = THREE.SRGBColorSpace;
      const mat = new THREE.MeshStandardMaterial({
        map: this.texture,
        roughness: 0.3,
      });
      this.surface.material = mat;
    }
  }

  dispose(): void {
    this.clear();
  }
}
